import farmLookups from '../public-lookups/farmLookups.json';

/**
 * FO-6 structure roof: shape in `building.roofShape`, material in `building.roofType`.
 *
 * Until #37180 an FO-6 structure had one "Roof type" select, bound to `roofType` but listing roof SHAPES
 * (farm.barnRoofType: Gable, Gambrel, Arched/Barrel, Gothic, Flat, Shed, Quonset, Hoop). `roofShape` was
 * never set, and the 360Value agricultural request reads only `roofShape`, so the shape staff picked never
 * reached 360Value. A structure now keeps its shape in `roofShape` (farm.barnRoofType, which is also
 * 360Value's agricultural `rooftype` list) and its material in `roofType` (farm.dwellingRoofType, as a
 * dwelling does and as the IMT importer writes since #37171).
 *
 * Structures saved before that still hold the shape in `roofType` with no `roofShape`. They are moved at
 * renewal, not in place, because a shape in `roofShape` reaches 360Value on the next re-run and can change
 * Coverage E (renewalVariables.farmRenewalCorrections). Until then every reader goes through these helpers.
 * `S` is both a barn shape (Shed Roof) and a material (Clay/Slate); a structure holding it with no shape is
 * one stored the old way (no importer writes `S` on a structure: #37171 maps slate to Other).
 */
export const FO6_ROOF_SHAPE_CODES: string[] = farmLookups.barnRoofType.map(({ id }) => id);

type StructureRoof = { roofType?: string | null; roofShape?: string | null } | null | undefined;

/** The structure still holds its roof shape in `roofType` (saved before #37180) and has no `roofShape`. */
export const holdsLegacyFo6RoofShape = (building: StructureRoof): boolean =>
  !building?.roofShape && !!building?.roofType && FO6_ROOF_SHAPE_CODES.includes(building.roofType);

/** The structure's roof shape (a farm.barnRoofType code), wherever it is stored, or null. */
export const fo6StructureRoofShape = (building: StructureRoof): string | null =>
  building?.roofShape || (holdsLegacyFo6RoofShape(building) ? building!.roofType! : null);

/** The structure's roof material (a farm.dwellingRoofType code), or null when it has none. */
export const fo6StructureRoofMaterial = (building: StructureRoof): string | null =>
  holdsLegacyFo6RoofShape(building) ? null : building?.roofType || null;
